import { createCommands } from '@tdp/common/commandHandler';
import { ILogger } from '@tdp/common/telemetry';

import { IAnalyticsV2Filters } from './abstractions/IAnalyticsFilters';
import { IAnalyticsService } from './abstractions/IAnalyticsService';
import {
  analyticsV2MetricsSlice,
  analyticsWidgetsSlice,
  analyticsV1MetricsSlice,
  appTypeSlice,
} from './reducer';
import { IAnalyticsUsageDataHook } from './useAnalyticsData';

export interface AnalyticsDataCommandHandlerDependencies {
  analyticsService: IAnalyticsService;
  logger: ILogger;
}

interface IGetMetricsDataParams {
  appDefinitionId: string;
  aggregationType: string;
  platform: string;
  operatingSystem: string;
  area: string;
}

interface IGetMonthlyActiveUsersParams {
  appDefinitionId: string;
}

interface IGetAppPublishTypeParams {
  externalAppId: string;
}

interface IGetAnalyticsDataParams {
  appId: string;
  filters: IAnalyticsV2Filters;
  params: IAnalyticsUsageDataHook;
}

export const createAppAnalyticsCommandHandler = ({
  analyticsService,
  logger,
}: AnalyticsDataCommandHandlerDependencies) =>
  createCommands({
    name: 'analyticsCommands',
    handlers: {
      async *getAnalyticsV2Data({
        appId,
        filters,
        params,
      }: IGetAnalyticsDataParams) {
        yield analyticsWidgetsSlice.actions.loadingData({ key: params.key });
        const analyticsV2DataResponse =
          await analyticsService.getAnalyticsV2Data(appId, filters, params);
        if (analyticsV2DataResponse.type === 'error') {
          if (
            analyticsV2DataResponse.preconditionError
          ) {
            yield analyticsV2MetricsSlice.actions.setPreconditionErrorAndClearData(
              analyticsV2DataResponse.preconditionError
            );
          }
          logger.error(
            'Error fetching analytics data for app id: %s | reason: %s',
            appId,
            analyticsV2DataResponse.errorMessage
          );
          yield analyticsWidgetsSlice.actions.loadingFailed({
            key: params.key,
          });
          return;
        }
        yield analyticsV2MetricsSlice.actions.analyticsAPISuccess(
          analyticsV2DataResponse.analyticsData
        );
        yield analyticsWidgetsSlice.actions.loadingSuccess({ key: params.key });
        yield analyticsWidgetsSlice.actions.setLastUpdated({
          apiResponse: analyticsV2DataResponse.analyticsData,
        });
      },

      async *getMetricsV1Data({
        appDefinitionId,
        aggregationType,
        platform,
        operatingSystem,
        area,
      }: IGetMetricsDataParams) {
        yield analyticsV1MetricsSlice.actions.metricsDataLoading({
          appId: appDefinitionId,
        });
        const getMetricsDataResponse = await analyticsService.getMetricsV1Data(
          appDefinitionId,
          aggregationType,
          platform,
          operatingSystem,
          area
        );
        if (getMetricsDataResponse.type === 'error') {
          logger.error(
            'Error fetching metrics data for app id: %s | reason: %s',
            appDefinitionId,
            getMetricsDataResponse.errorMessage
          );
          yield analyticsV1MetricsSlice.actions.getMetricsDataFailed({
            appId: appDefinitionId,
          });
          return;
        }
        yield analyticsV1MetricsSlice.actions.getMetricsDataSuccess({
          appId: appDefinitionId,
          metricsData: getMetricsDataResponse.metricsData,
        });
      },

      async *getMetricsV1MonthlyActiveUsers({
        appDefinitionId,
      }: IGetMonthlyActiveUsersParams) {
        yield analyticsV1MetricsSlice.actions.activeUserDataLoading({
          appId: appDefinitionId,
        });
        const getMonthlyActiveUsersResponse =
          await analyticsService.getMetricsV1MonthlyActiveUsers(
            appDefinitionId
          );
        if (getMonthlyActiveUsersResponse.type === 'error') {
          logger.error(
            'Error fetching metrics data for app id: %s | reason: %s',
            appDefinitionId,
            getMonthlyActiveUsersResponse.errorMessage
          );
          yield analyticsV1MetricsSlice.actions.getActiveUserDataFailed({
            appId: appDefinitionId,
          });
          return;
        }
        yield analyticsV1MetricsSlice.actions.getActiveUserDataSuccess({
          appId: appDefinitionId,
          activeUserData: getMonthlyActiveUsersResponse.activeUserData,
        });
      },

      async *getAppPublishType({ externalAppId }: IGetAppPublishTypeParams) {
        const getAppPublishTypeResponse =
          await analyticsService.getAppPublishType(externalAppId);
        if (getAppPublishTypeResponse.type === 'error') {
          logger.error(
            'Error fetching app publish data for app id: %s | reason: %s',
            externalAppId,
            getAppPublishTypeResponse.errorMessage
          );
          yield appTypeSlice.actions.getAppPublishTypeFail();
          return;
        }
        yield appTypeSlice.actions.getAppPublishTypeSuccess(
          getAppPublishTypeResponse.appPublishCDNStatus
        );
      },
    },
  });
